/**
 * SLICE 48A — the staff data export, and the live export state the release dialog
 * reads instead of reciting a warning.
 *
 * Mirrors the wire contract in `api/src/modules/admin/routes.ts`
 * (`/admin/tenants/:tenantId/reports/:kind/export.csv` and `/export-status`).
 */
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiFetch, apiJson, ApiError } from '@/lib/apiClient';

/** The catalogue's kinds, mirrored. A kind the server does not know 404s. */
export const EXPORT_KINDS = [
  'trips',
  'stops',
  'distance',
  'utilisation',
  'speeding',
  'zones',
  'events',
  'maintenance',
] as const;
export type ExportKind = (typeof EXPORT_KINDS)[number];

export interface ExportStatus {
  /** ISO of the most recent RECORDED staff export, or null if none ever ran. */
  lastExportAt: string | null;
}

export const exportStatusKey = (tenantId: string) => ['admin', 'export-status', tenantId] as const;

export function useExportStatus(tenantId: string): UseQueryResult<ExportStatus, Error> {
  return useQuery({
    queryKey: exportStatusKey(tenantId),
    queryFn: () => apiJson<ExportStatus>(`/api/admin/tenants/${tenantId}/export-status`),
  });
}

const ROW_COUNT_HEADER = 'X-Report-Row-Count';

/**
 * 🔴 QUOTE-AWARE, AND A NAIVE `split('\n')` IS WRONG HERE — REVIEW FINDING F1.
 *
 * This is the EXACT twin of `countCsvDataRows` in
 * `web/src/features/reports/useReportLibrary.ts`, and it is duplicated rather
 * than imported because `admin` and `web` are separate workspaces (the same
 * reason `api/src/modules/reports/csv.ts` is a deliberate byte-compatible twin
 * of the web serialiser). **Whoever edits either must edit both.**
 *
 * WHY IT CANNOT BE `text.split('\n').length`: the server quotes any field
 * containing a comma, a quote or a CR/LF (`api/src/modules/reports/csv.ts`), so
 * a zone name, a vehicle label, a Somali place name or a maintenance note may
 * legitimately carry an embedded newline INSIDE a quoted field. A naive count
 * OVER-counts those rows, `actual !== expected`, and the download then refuses a
 * COMPLETE file as truncated — inverting the very contract it implements, on the
 * one route whose whole purpose is a departing customer's complete data.
 *
 * The first draft of this file did exactly that, and its comment claimed it was
 * "the same one the customer client runs". It was not. The discriminating case
 * is a cell containing a newline, and `useTenantExport.test.ts` pins it — every
 * newline-free fixture agrees between the correct and the broken counter, which
 * is why this needed a test chosen to force disagreement rather than more rows.
 */
export function countCsvDataRows(text: string): number {
  let records = 0;
  let inQuotes = false;
  let sawContent = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') i++; // escaped quote — skip the pair
        else inQuotes = false;
      }
      sawContent = true;
    } else if (ch === '"') {
      inQuotes = true;
      sawContent = true;
    } else if (ch === '\n') {
      records++;
      sawContent = false;
    } else if (ch !== '\r') {
      sawContent = true;
    }
  }
  // A final record with no trailing newline still counts (our export always
  // terminates every row, so this is belt-and-braces).
  if (sawContent) records++;
  return Math.max(0, records - 1); // minus the header row
}

function saveBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Download ONE report kind for a tenant, as staff.
 *
 * 🔴 THE TRUNCATION CHECK IS NOT OPTIONAL. A page query that fails mid-stream
 * closes the HTTP body cleanly, so a SHORT file resolves and looks whole. The
 * server sends the exact expected data-row count in a header before the body; we
 * refuse any file whose decoded row count differs.
 *
 * The counter is the quote-aware twin of the customer client's — see the block on
 * `countCsvDataRows` above for why a naive line split refuses COMPLETE files.
 *
 * 🔴 `includeWithdrawn` defaults TRUE server-side. That is the whole point: a
 * released fleet's vehicles are `hidden_at`, and every customer-facing report
 * excludes them, so an export run after release would silently omit exactly the
 * vehicles being handed over.
 */
export async function downloadTenantExport(
  tenantId: string,
  kind: ExportKind,
  range: { from: string; to: string },
): Promise<void> {
  const qs = new URLSearchParams({ from: range.from, to: range.to });
  const res = await apiFetch(
    `/api/admin/tenants/${tenantId}/reports/${kind}/export.csv?${qs.toString()}`,
  );
  if (!res.ok) throw new ApiError(res.status, { error: `admin_export_failed_${res.status}` });

  const blob = await res.blob();
  const expectedRaw = res.headers.get(ROW_COUNT_HEADER);
  if (expectedRaw !== null) {
    const expected = Number(expectedRaw);
    const actual = countCsvDataRows(await blob.text());
    if (!Number.isFinite(expected) || actual !== expected) {
      // The file is SHORT. Refusing is the whole point: a partial export that
      // resolves cleanly is how someone bills off an incomplete history.
      throw new ApiError(res.status, {
        error: 'admin_export_truncated',
        expected: expectedRaw,
        got: actual,
      });
    }
  }
  const day = range.from.slice(0, 10);
  const end = range.to.slice(0, 10);
  saveBlob(`raad-${kind}-${day}_${end}.csv`, blob);
}

/** Invalidate the status after an export, so the dialog's live state follows. */
export function useInvalidateExportStatus(tenantId: string): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: exportStatusKey(tenantId) });
}

/**
 * SLICE 11b-export — download the departing customer's whole archive.
 *
 * One zip: the report CSVs, their own zone definitions, and a manifest recording
 * per entry what the database held and what was written. The server refuses on an
 * open account (409 `tenant_not_closed`), because a still-ingesting tenant makes
 * the completeness arithmetic race.
 *
 * 🔴 `X-Raad-Export-Complete` travels with the bytes, so an incomplete archive is
 * visible to the operator here rather than only in the audit trail — and it is the
 * SAME fact slice 48B's gate reads. A file that arrives with `false` will not
 * unlock release, and the operator should learn that at download time, not after
 * typing the tenant name.
 */
export async function downloadTenantArchive(
  tenantId: string,
): Promise<{ complete: boolean }> {
  const res = await apiFetch(`/api/admin/tenants/${tenantId}/export.zip`);
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* a non-JSON error body is still an error; the status carries the meaning */
    }
    throw new ApiError(res.status, body);
  }
  const complete = res.headers.get('X-Raad-Export-Complete') === 'true';
  const blob = await res.blob();
  const disp = res.headers.get('Content-Disposition') ?? '';
  const named = /filename="([^"]+)"/.exec(disp)?.[1];
  saveBlob(named ?? `raad-export-${tenantId}.zip`, blob);
  return { complete };
}

/** The four reasons `release-fleet` can refuse, as the server names them. Kept as
 *  a union so a new server reason cannot silently render as a blank dialog. */
export type ReleaseBlockedReason =
  | 'tenant_not_closed'
  | 'export_missing'
  | 'export_incomplete'
  | 'export_stale';

const REASONS: readonly string[] = [
  'tenant_not_closed',
  'export_missing',
  'export_incomplete',
  'export_stale',
];

/** Pull the refusal reason out of a 409 body, or null if this was some other
 *  failure. Never guesses: an unrecognised reason returns null and the caller
 *  falls back to the generic failure copy rather than inventing an explanation. */
export function releaseBlockedReason(err: unknown): ReleaseBlockedReason | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as { error?: string; reason?: string } | null;
  if (!body || body.error !== 'release_blocked') return null;
  return REASONS.includes(body.reason ?? '') ? (body.reason as ReleaseBlockedReason) : null;
}
